import {
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  useParams,
} from "@tanstack/react-router";
import type { ReactNode } from "react";

import indexCss from "../../../index.css?url";
import { App } from "../../app";

import { defineOpenGraphProperty, getI18n, getLanguageFromParams } from "./{-$locale}/-common";

import { COMPANY_INFO } from "~/entities/company";
import type { UserRoleEntity } from "~/entities/user-role";
import ogImage from "~/shared/assets/og-image.jpg";
import type { Config } from "~/shared/config";
import { createTc, Tk } from "~/shared/i18n";
import { EMPTY, QUESTION_MARK } from "~/shared/str";

type RootDocumentProps = {
  children?: ReactNode | undefined;
};

function RootDocument({ children }: RootDocumentProps) {
  const { locale } = useParams({ from: "/{-$locale}" });

  return (
    <html lang={locale}>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function Component() {
  const { config } = Route.useRouteContext();

  return (
    <RootDocument>
      <App config={config} />
    </RootDocument>
  );
}

type Context = {
  config: Config<
    typeof UserRoleEntity.zFlatRoleByRoleId.keySchema,
    typeof UserRoleEntity.zFlatRoleByRoleId.valueSchema
  >;
};

export const Route = createRootRouteWithContext<Context>()({
  loader: ({ context }) => context,
  head: ({
    params,
    loaderData: { config: { google: { googleConversionTagId = EMPTY } = {} } = {} } = {},
  }) => {
    const locale = getLanguageFromParams(params);

    const i18n = getI18n(locale);
    const tc = createTc(i18n.t.bind(i18n));

    return {
      links: [
        {
          rel: "stylesheet",
          href: indexCss,
        },
        {
          rel: "icon",
          type: "image/svg+xml",
          href: "/logo.svg",
        },
        {
          rel: "preconnect",
          href: "https://fonts.googleapis.com",
        },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&family=Roboto:ital,wght@0,100..900;1,100..900&display=swap",
        },
      ],
      meta: [
        {
          charSet: "utf-8",
        },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1.0",
        },
        {
          title: COMPANY_INFO.name,
        },
        {
          name: "description",
          content: tc(Tk["PROFESSIONAL_ORGANIZATION_OF_MOVING_TO_FROM_..."]),
        },
        {
          name: "keywords",
          content: tc(Tk["MOVING_IN_SWITZERLAND_APARTMENT_MOVING_OFFICE..."]),
        },
        ...[
          {
            property: "title",
            content: COMPANY_INFO.name,
          },
          {
            property: "description",
            content: tc(Tk["PROFESSIONAL_ORGANIZATION_OF_MOVING_TO_FROM_..."]),
          },
          {
            property: "type",
            content: "website",
          },
          {
            property: "image",
            content: ogImage,
          },
        ].map(defineOpenGraphProperty),
      ],
      scripts: [
        {
          children: `
window.dataLayer = window.dataLayer || [];
function gtag() {
  dataLayer.push(arguments);
}
          `,
        },
        {
          "data-cookieconsent": "ignore",
          children: `
gtag("consent", "default", {
  ad_personalization: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  analytics_storage: "denied",
  functionality_storage: "granted",
  personalization_storage: "granted",
  security_storage: "granted",
  wait_for_update: 500,
});
gtag("set", "ads_data_redaction", true);
gtag("set", "url_passthrough", false);
          `,
        },
        {
          src: [
            "https://www.googletagmanager.com/gtag/js",
            new URLSearchParams({
              id: googleConversionTagId,
            }).toString(),
          ].join(QUESTION_MARK),
          async: true,
        },
        {
          children: `
gtag("js", new Date());
gtag("config", "${googleConversionTagId}");
          `,
        },
      ],
    };
  },
  component: Component,
});
